import React, { useMemo, Fragment } from 'react';
import { ScissorsIcon } from 'lucide-react';
import cvData from '../../data/cv';
import { Masthead } from './Masthead';
import { Rubric } from './Rubric';
import { ExperienceEntry } from './ExperienceEntry';
import { ProfileBlock, CapabilitiesBlock, EducationBlock } from './Aside';
import { EarlierExperience, type CondensedRole } from './EarlierExperience';
import { Page } from './Page';
import { periodSortKey, condenseRole } from './helpers';
import { useMeasuredPages, type Block } from './useMeasuredPages';
// A4 content area height: 297mm − 28mm margins ≈ 1017px @96dpi, minus the
// page footer (~46px) so packed content never overflows into it.
const PAGE_HEIGHT_PX = 928;
const CONTENT_WIDTH_PX = 680;
export function CVSheet({
  compact = false
}: {
  compact?: boolean;
}) {
  const {
    experience,
    earlierExperience,
    meta
  } = cvData;
  const S = meta.sections;
  // Condensed mode: a fuller detailed set (spilling Earlier Experience onto a
  // third page) reads better than cramming everything into two pages.
  const detailedCount = compact ? 5 : meta.detailedRoleCount;
  const roles = useMemo(() => [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period)), [experience]);
  const detailed = useMemo(() => roles.slice(0, detailedCount), [roles, detailedCount]);
  const condensed: CondensedRole[] = useMemo(() => [...roles.slice(detailedCount).map(condenseRole), ...earlierExperience], [roles, detailedCount, earlierExperience]);
  const blocks: Block[] = useMemo(() => {
    const list: Block[] = [];
    const gap = compact ? 'mt-4' : 'mt-11';
    // Masthead, then a balanced two-column intro band: Profile (left) +
    // Capabilities (right). Kept as one block for a cohesive header.
    list.push({
      key: 'intro',
      node: <>
          <Masthead compact={compact} data-id="element-59" data-mp="29td1dysxgl4d" />
          <div className={`${compact ? 'mt-4 gap-x-8 gap-y-4' : 'mt-10 gap-x-12 gap-y-10'} grid grid-cols-12`} data-id="element-60" data-mp="29ue9pvik6tie">
            <div className={compact ? 'col-span-5' : 'col-span-7'} data-id="element-61" data-mp="21urjk2qv0k6n">
              <ProfileBlock compact={compact} data-id="element-62" data-mp="1wuvo4rw8a12p" />
            </div>
            <div className={compact ? 'col-span-7' : 'col-span-5'} data-id="element-63" data-mp="2bre1ojrswx4z">
              <CapabilitiesBlock compact={compact} data-id="element-64" data-mp="2boi3cuc1k6g0" />
            </div>
          </div>
        </>
    });
    list.push({
      key: 'exp-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-65" data-mp="2kpg9bi61io18">
          <Rubric index={S.experience.index} title={S.experience.title} count={`${detailed.length} recent roles`} compact={compact} data-id="element-66" data-mp="2fsptmvy5dugc" />
        </div>
    });
    detailed.forEach((item, i) => {
      list.push({
        key: `role-${i}`,
        node: <ExperienceEntry item={item} isLast compact={compact} data-id="element-67" data-mp="33hiu95ppsqha" />
      });
    });
    if (!compact) {
      // Full mode: each earlier role as its own condensed row.
      list.push({
        key: 'earlier-head',
        keepWithNext: true,
        node: <div className={gap} data-id="element-68" data-mp="3i2mx9ce3dan6">
            <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} data-id="element-69" data-mp="3n3kcbg3t1okg" />
          </div>
      });
      condensed.forEach((r, i) => {
        list.push({
          key: `cond-${i}`,
          node: <EarlierExperience roles={[r]} data-id="element-70" data-mp="2ze87esfig2ny" />
        });
      });
    } else if (condensed.length > 0) {
      // Condensed mode: Earlier Experience (left) + Education (right) share one
      // two-column block, forced to start cleanly at the top of a new page.
      list.push({
        key: 'tail',
        breakBefore: true,
        node: <div className={gap} data-id="element-71" data-mp="2ug6nk5a9fzbf">
            <div className="grid grid-cols-12 gap-x-10" data-id="element-72" data-mp="2ufevh4fvgjpw">
              <div className="col-span-8" data-id="element-73" data-mp="2gf40pqbfykzg">
                <Rubric index={S.earlier.index} title={S.earlier.title} count={`${condensed.length} roles`} compact={compact} data-id="element-74" data-mp="2ghgr8rv9m3o4" />
                <ul className="divide-y divide-[var(--line)]" data-id="element-75" data-mp="3ntpommkqu8nb">
                  {condensed.map((r, i) => <li key={i} className="grid grid-cols-[112px_1fr] items-baseline gap-4 py-1.5 first:pt-0" data-id="element-76" data-mp="3ezb8khse2u35" data-mpi={i}>
                      <span className="whitespace-nowrap font-mono text-[8.5px] tabular-nums tracking-tight text-[var(--ink-faint)]" data-id="element-77" data-mp="39ydou0rqdrzj">
                        {r.period}
                      </span>
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5" data-id="element-78" data-mp="304epf9j1kmba">
                        <span className="font-display text-[12px] font-medium leading-snug text-[var(--ink)]" data-id="element-79" data-mp="2w0yuqiqj8w18">
                          {r.company}
                        </span>
                        <span className="font-mono text-[8.5px] uppercase tracking-[0.08em] text-[var(--accent)]" data-id="element-80" data-mp="u80wu8hwfd9f">
                          {r.role}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </div>
              <div className="col-span-4" data-id="element-81" data-mp="11rk1a3qb49b1">
                <EducationBlock compact={compact} data-id="element-82" data-mp="11qidcyo6zsbw" />
              </div>
            </div>
          </div>
      });
      return list;
    }
    list.push({
      key: 'edu-head',
      keepWithNext: true,
      node: <div className={gap} data-id="element-83" data-mp="dv5veu91r1li" />
    });
    list.push({
      key: 'edu',
      node: <EducationBlock compact={compact} data-id="element-84" data-mp="dx0fk6chupoj" />
    });
    return list;
  }, [detailed, condensed, S, compact]);
  const {
    setRef,
    pages
  } = useMeasuredPages(blocks, PAGE_HEIGHT_PX, String(compact));
  // Hidden measuring layer. offsetHeight is transform-independent, so measuring
  // works even though CVSheet renders inside the zoom/pan transform. Rendered as
  // a plain absolutely-positioned sibling (no portal needed).
  const measuringLayer = <div aria-hidden="true" className="no-print" style={{
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: '-99999px',
    top: 0,
    width: `${CONTENT_WIDTH_PX}px`
  }} data-id="element-85" data-mp="238j4ews3tdsy">
      {blocks.map(b => <div key={b.key} ref={setRef(b.key)} className="mb-5" data-id="element-86" data-mp="d4pn3gjugslm">
          {b.node}
        </div>)}
    </div>;
  const total = pages.length;
  return <div className="relative flex flex-col items-center" data-id="element-87" data-mp="3gmh8gc3h6eau">
      {measuringLayer}
      {pages.map((pageBlocks, pageIdx) => <Fragment key={pageIdx} data-id="element-88" data-mp="3cloythqoy7e1" data-mpi={pageIdx}>
          {/* On-screen page-break divider (never printed). */}
          {pageIdx > 0 && <div className="no-print flex w-[210mm] max-w-full items-center gap-3 py-5" aria-hidden="true" data-id="element-89" data-mp="3qdvdt0i3y8bw">
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-90" data-mp="2xvq27jesb40o" />
              <span className="flex items-center gap-1.5 rounded-full border border-[var(--line-strong)] bg-white/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--ink-faint)]" data-id="element-91" data-mp="2xtwus1wckfrm">
                <ScissorsIcon className="h-3 w-3" data-id="element-92" data-mp="18crm6oidq2wu" />
                Page {pageIdx + 1} of {total}
              </span>
              <span className="h-px flex-1 bg-[var(--line-strong)]" data-id="element-93" data-mp="ze6753c4960t" />
            </div>}
          <Page index={pageIdx + 1} total={total} data-id="element-94" data-mp="14daerpje6vp1">
            <div className={compact ? 'space-y-3' : 'space-y-5'} data-id="element-95" data-mp="14e26gp9vc9a1">
              {pageBlocks.map(b => <div key={b.key} data-id="element-96" data-mp="1s3d4w03qxrl0">{b.node}</div>)}
            </div>
          </Page>
        </Fragment>)}
    </div>;
}